import React, { createContext, useContext } from "react"

export interface EditingContextValue {
  /** True when this subtree renders inside an editor preview (not the live page). Default false. */
  editing: boolean
  /**
   * Called when a live action is attempted in editing mode (save, react, add example, rate, …). The
   * editor decides what to do: open the relevant edit panel, or toast "not available in preview".
   * Receives an action key so the editor can route specific ones (e.g. "add_example").
   */
  notify?: (action: string) => void
}

const EditingContext = createContext<EditingContextValue>({ editing: false })

export const EditingProvider: React.FC<React.PropsWithChildren<EditingContextValue>> = ({
  editing,
  notify,
  children,
}) => (
  <EditingContext.Provider value={{ editing, notify }}>{children}</EditingContext.Provider>
)

/**
 * Read the editing state. Templates use this to render static/disabled versions of live actions —
 * a common pattern like a loading skeleton. `editing` is false on the real page (no provider) and
 * true inside an editor preview. In editing mode, wire interactive controls to `notify(action)`
 * instead of firing, so the editor can open a panel or toast.
 */
export const useEditing = (): EditingContextValue => useContext(EditingContext)

export default EditingProvider
